// 261015 P4c-1 (Q17/Q18) — mã lỗi API của tổng quan / chốt ② ⇒ MỘT câu tiếng Việt + MỘT lối ra. THUẦN (jest). Không import antd.
// Dùng cho: chốt/bỏ chốt/"Duyệt & chạy tiếp", tạo tổng quan, PATCH sửa tổng quan, Trợ lý ② (`approve_skeleton`).
import { friendlyApiError } from '../jobErrorText';
import type { OverviewWriterErrorCode } from '../../../../types/adaptation';
import { MERGE_OUTDATED_TEXT, SKELETON_CHANGED_TEXT, STALE_CONTENT_TEXT, STALE_READER_TEXT } from './overviewView';

/** Lối ra mà giao diện có thể làm ngay: tải lại · tạo lại · gộp lại · mở chỗ cần sửa · hỏi lại trợ lý · chờ job · (261015 P4c-2b) mở Tổng quan truyện từ chặng khác. */
export type ErrorExit = 'reload' | 'regenerate' | 'merge' | 'fix' | 'askAssistant' | 'wait' | 'openOverview' | 'none';

export interface ErrorView {
  code: string;
  text: string;
  /** Mỗi dòng chi tiết (vd các vấn đề trong `details.issues`). */
  extra: string[];
  exit: ErrorExit;
  exitLabel: string;
}

export const EXIT_LABEL: Record<ErrorExit, string> = {
  reload: 'Tải lại', regenerate: 'Tạo lại tổng quan', merge: 'Gộp lại tổng quan', fix: 'Xem các chỗ cần sửa', askAssistant: 'Hỏi lại trợ lý', wait: 'Chờ việc nền xong', openOverview: 'Mở Tổng quan truyện', none: '',
};

interface Raw { status?: number; code: string; message: string; details: any; where: string; violations: Array<{ message?: string }> }

const rawOf = (e: unknown): Raw => {
  const r = (e as { response?: { status?: number; data?: any } } | null | undefined)?.response;
  const d = r?.data ?? {};
  return {
    status: r?.status, code: typeof d.code === 'string' ? d.code : '', message: typeof d.message === 'string' ? d.message : '',
    details: d.details ?? null, where: typeof d.where === 'string' ? d.where : '', violations: Array.isArray(d.violations) ? d.violations : [],
  };
};

const issueLines = (details: any): string[] => (Array.isArray(details?.issues) ? details.issues : [])
  .map((i: { message?: unknown }) => String(i?.message ?? '').trim()).filter(Boolean).slice(0, 5);

const staleText = (details: any): string => {
  const causes: string[] = Array.isArray(details?.issues) ? details.issues.flatMap((i: any) => (Array.isArray(i?.detail?.causes) ? i.detail.causes : [])) : [];
  const c = causes.includes('CONTENT');
  const r = causes.includes('READER');
  if (c && r) return `${STALE_CONTENT_TEXT} ${STALE_READER_TEXT}`;
  if (c) return STALE_CONTENT_TEXT;
  if (r) return STALE_READER_TEXT;
  return 'Nguyên tác hoặc cách đọc đã đổi — tạo lại tổng quan.';
};

export interface ErrorCtx {
  /** `assistant` ⇒ lỗi của nút do Trợ lý đề xuất: lối ra luôn là "hỏi lại trợ lý" (đề xuất cũ không còn đúng). */
  via?: 'ui' | 'assistant';
}

const view = (code: string, text: string, exit: ErrorExit, extra: string[] = []): ErrorView => ({ code, text, extra, exit, exitLabel: EXIT_LABEL[exit] });

/** 261015 P4c-2b (D9b) — chốt dàn tập / chia tập / viết lại tập bị từ chối vì truyện CÓ tổng quan mà tổng quan chưa chốt hoặc đã cũ. BE từ chối TRƯỚC khi ghi và trước khi gọi AI. */
export const OVERVIEW_NOT_EFFECTIVE_TEXT = 'Tổng quan truyện chưa chốt hoặc đã cũ — chốt lại tổng quan ở ② trước khi chốt dàn tập, chia tập hay nhờ AI viết lại tập. Chưa có gì được ghi, chưa tốn AI.';

/**
 * 261015 P4c-2c-1 (D10) — `OVERVIEW_CHANGED` (409): tổng quan đổi TRONG lúc viết một tập, hoặc sau khi xem thử khối ⑤ (lúc Áp / Áp lại). BE từ chối TRƯỚC khi ghi (0 kịch bản / phiên bản / biên lai mới);
 * lối ra = mở Tổng quan truyện kiểm tra (chốt lại nếu cần) rồi viết / xem thử lại. Dùng chung cho Viết tập/lô · Viết lại qua trợ lý · Áp / Áp lại khối.
 */
export const OVERVIEW_CHANGED_TEXT = 'Tổng quan truyện vừa đổi — trong lúc viết hoặc sau khi xem thử. Chưa có gì được ghi — mở Tổng quan truyện kiểm tra (chốt lại nếu cần) rồi viết hoặc xem thử lại.';

/** Hai mã 409 của các cửa viết / sửa cảnh bằng AI ở ⑤ khi truyện CÓ tổng quan. Cùng lối ra `openOverview`. */
export const OVERVIEW_WRITER_CODES: ReadonlyArray<OverviewWriterErrorCode> = ['OVERVIEW_NOT_EFFECTIVE', 'OVERVIEW_CHANGED'];
export const isOverviewWriterCode = (code: unknown): code is OverviewWriterErrorCode => OVERVIEW_WRITER_CODES.includes(code as OverviewWriterErrorCode);

/** Nguyên nhân cụ thể từ `details.overviewState` của BE (mỗi nguyên nhân một dòng; không rõ nguyên nhân mà `approved:false` ⇒ "chưa chốt"). */
export const overviewNotEffectiveLines = (details: any): string[] => {
  const s = details?.overviewState;
  if (!s || typeof s !== 'object') return [];
  const out: string[] = [];
  if (s.stale === true) out.push('Nguyên tác hoặc cách đọc đã đổi — tạo lại tổng quan.');
  if (s.skeletonChanged === true) out.push(SKELETON_CHANGED_TEXT);
  if (s.mergeOutdated === true) out.push(`${MERGE_OUTDATED_TEXT}.`);
  if (Number(s.unverifiedCount) > 0) out.push(`Còn ${Number(s.unverifiedCount)} ý sự việc chưa tìm thấy trích trong nguyên tác.`);
  if (!out.length && s.approved === false) out.push('Tổng quan chưa được chốt — bấm "Chốt tổng quan" ở ②.');
  return out;
};

/** Mã lỗi (đã có) → câu + lối ra. `fallback` dùng khi không nhận ra mã (câu lỗi BE đã lọc thuật ngữ kỹ thuật bởi `friendlyApiError`). */
export const overviewErrorView = (e: unknown, fallback: string, ctx: ErrorCtx = {}): ErrorView => {
  const r = rawOf(e);
  const asking = ctx.via === 'assistant';
  const ask = (code: string, text: string, extra: string[] = []): ErrorView => view(code, `${text} — hãy hỏi lại trợ lý.`, 'askAssistant', extra);
  switch (r.code) {
    case 'EXPECTED_MODIFIED_AT_REQUIRED': case 'INVALID_EXPECTED_MODIFIED_AT':
      return asking ? ask(r.code, 'Đề xuất thiếu mốc thời gian của bản khung xương để so khớp') : view(r.code, 'Thiếu mốc thời gian của bản đang xem để so khớp — tải lại trang rồi thử lại.', 'reload');
    case 'OUTLINE_CHANGED':
      return asking ? ask(r.code, 'Khung xương vừa bị đổi ở nơi khác nên đề xuất này không còn khớp') : view(r.code, 'Khung xương vừa được đổi ở nơi khác (hoặc ở tab khác) — đã tải lại, xem lại rồi thử lại.', 'reload');
    case 'ACTION_STALE':
      return ask(r.code, 'Đề xuất của trợ lý không còn dùng được (dữ liệu đã đổi)');
    case 'OVERVIEW_MISSING':
      return asking ? ask(r.code, 'Chưa có tổng quan truyện') : view(r.code, 'Chưa có tổng quan truyện — tạo tổng quan trước (tab đầu của ②) rồi mới chốt được.', 'regenerate');
    case 'OVERVIEW_INCOMPLETE':
      return asking ? ask(r.code, 'Tổng quan còn thiếu quyển', issueLines(r.details)) : view(r.code, 'Tổng quan còn thiếu quyển chưa đọc được — "Tạo lại" để đọc các quyển thiếu rồi chốt.', 'regenerate', issueLines(r.details));
    case 'OVERVIEW_SOURCE_STALE':
      return asking ? ask(r.code, staleText(r.details).replace(/\.$/, '')) : view(r.code, staleText(r.details), 'regenerate', []);
    case 'OVERVIEW_MERGE_OUTDATED':   // 261015 P4c-2a: BE trả mã này chỉ khi nguồn KHÔNG lệch (SOURCE_STALE đứng trước) ⇒ lối ra "Gộp lại" dùng được
      return asking ? ask(r.code, 'Cách gộp tổng quan đã cập nhật') : view(r.code, `${MERGE_OUTDATED_TEXT} (chỉ gộp lại, không đọc lại nguyên tác) để có Động cơ hấp dẫn.`, 'merge');
    case 'OVERVIEW_SKELETON_CHANGED':
      return asking ? ask(r.code, 'Khung xương đã đổi sau khi gộp tổng quan') : view(r.code, SKELETON_CHANGED_TEXT, 'merge');
    case 'OVERVIEW_REFERENCE_BROKEN': {
      const broken: string[] = Array.isArray(r.details?.issues) ? r.details.issues.flatMap((i: any) => (Array.isArray(i?.detail?.broken) ? i.detail.broken : [])) : [];
      return asking ? ask(r.code, 'Tổng quan còn tham chiếu hỏng', broken.slice(0, 5))
        : view(r.code, 'Tổng quan còn tham chiếu hỏng (hồ sơ trỏ nhân vật đã mất, quan hệ thiếu đầu, ý không còn…) — sửa ở tab "Tổng quan truyện" hoặc "Gộp lại tổng quan".', 'fix', broken.slice(0, 5));
    }
    case 'OVERVIEW_UNVERIFIED': {
      const ids: string[] = Array.isArray(r.details?.issues) ? r.details.issues.flatMap((i: any) => (Array.isArray(i?.detail?.claimIds) ? i.detail.claimIds : [])) : [];
      const t = `Còn ${ids.length || 'một số'} ý sự việc chưa tìm thấy trích trong nguyên tác`;
      return asking ? ask(r.code, t) : view(r.code, `${t} — sửa trích hoặc xoá ý rồi chốt.`, 'fix', ids.length ? [`Ý: ${ids.slice(0, 12).join(', ')}${ids.length > 12 ? '…' : ''}`] : []);
    }
    case 'NEEDS_APPROVAL':
      return view(r.code, 'Khung xương đã bị sửa về bản nháp trong lúc dừng — chốt lại ở ② (nút "Duyệt khung xương" / "Chốt tổng quan") rồi bấm "Chạy tiếp".', 'fix');
    case 'OVERVIEW_LIMIT_EXCEEDED':
      return view(r.code, `Vượt giới hạn của tổng quan${r.where ? ` (${r.where})` : ''} — rút gọn rồi lưu lại; không có gì được ghi.`, 'fix');
    case 'OVERVIEW_JOB_RUNNING':
      return view(r.code, 'Đang tạo tổng quan ở nền — chờ xong rồi mới sửa tay, nếu không bản sửa sẽ bị ghi đè.', 'wait');
    case 'JOB_RUNNING':
      return view(r.code, 'Truyện đang có tiến trình dựng / chia tập / bản địa hoá chạy — chờ xong rồi thử lại.', 'wait');
    case 'OVERVIEW_SOURCE_TOO_LONG': {
      const chars = Number(r.details?.chars);
      const max = Number(r.details?.max);
      return view(r.code, Number.isFinite(chars) && Number.isFinite(max) ? `Nguyên tác quá dài để tạo tổng quan (${chars.toLocaleString('vi-VN')} ký tự, tối đa ${max.toLocaleString('vi-VN')}).` : 'Nguyên tác quá dài để tạo tổng quan một lượt.', 'none');
    }
    case 'OVERVIEW_SOURCE_EMPTY': return view(r.code, 'Truyện chưa có chương nào để đọc — nạp chương ở ① trước.', 'none');
    case 'INVALID_OVERVIEW_MODE': return view(r.code, 'Chế độ tạo tổng quan không hợp lệ — tải lại trang rồi thử lại.', 'reload');
    case 'OUTLINE_MISSING': return view(r.code, 'Chưa có khung xương — dựng khung xương trước.', 'none');
    case 'OVERVIEW_NOT_EFFECTIVE':   // 261015 P4c-2b: lối ra là CHỖ SỬA (tổng quan ở ②), kể cả khi lỗi tới qua Trợ lý — hỏi lại trợ lý không làm tổng quan có hiệu lực
      return view(r.code, OVERVIEW_NOT_EFFECTIVE_TEXT, 'openOverview', overviewNotEffectiveLines(r.details));
    case 'OVERVIEW_CHANGED':   // 261015 P4c-2c-1: như NOT_EFFECTIVE — lối ra là CHỖ SỬA (tổng quan ở ②), kể cả khi lỗi tới qua Trợ lý
      return view(r.code, OVERVIEW_CHANGED_TEXT, 'openOverview');
    default: break;
  }
  // Lỗi lint khi chốt (400 + violations): giữ danh sách vi phạm.
  if (r.status === 400 && r.violations.length) {
    return view('LINT', friendlyApiError(e, 'Khung xương còn lỗi phải xử trước khi duyệt'), 'fix', r.violations.map((v) => String(v?.message ?? '').trim()).filter(Boolean).slice(0, 6));
  }
  return view(r.code || 'UNKNOWN', friendlyApiError(e, fallback), 'none');
};

/** Mã lỗi nào nên tải lại dữ liệu sau khi báo (bản đang hiện đã cũ). */
export const errorShouldReload = (v: ErrorView): boolean => v.exit === 'reload' || v.exit === 'openOverview' || v.exit === 'regenerate' || v.exit === 'merge' || v.exit === 'fix' || v.exit === 'askAssistant';

